import {
  AUTOMATED_TASK_TYPES,
  ISO_WEEKDAYS,
  MANUAL_TASK_TYPES,
  TASK_TYPE_LABELS,
  TEMPLATE_ENTITIES_BY_SUBJECT,
  TEMPLATE_FIELDS,
  TRIGGER_DATE_FIELDS,
  TRIGGER_DEFAULT_OPEN_MINUTE,
  TRIGGER_MAX_DELAY_MINUTES,
  WORKING_WEEKDAYS,
  triggerInputSchema,
  triggerPayloadExample,
  type TriggerRunExisting,
  type TriggerSubject,
} from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Copy, KeyRound, Pencil, Plus, Trash2, Zap } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ApiError, describeError } from '../../lib/api.ts';
import {
  api,
  type PipelineStageView,
  type PipelineView,
  type TriggerBody,
  type TriggerView,
  type TriggerWithSecretView,
} from '../../lib/client.ts';
import { useMembers } from '../../lib/record-data.ts';
import { useSession } from '../../lib/session.tsx';
import { cn } from '../../lib/utils.ts';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { Input, Textarea, nativeSelectClass } from '../ui/input.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';
import { selectTriggerClass } from '../ui/select.tsx';
import type { FilterOption } from '../record-table/filter-bar.tsx';
import { OptionSearch, type OptionGroup } from '../record-table/option-search.tsx';

// A stage's automations (pipeline triggers): the stage's button opens the list, the list the editor.
const queryKey = (pipelineId: string) => ['pipelines', 'triggers', pipelineId] as const;

/** The stage row's button: a lightning bolt and how many automations the stage has. */
export function StageAutomationsButton({
  pipeline,
  stage,
}: {
  pipeline: PipelineView;
  stage: PipelineStageView;
}) {
  const [open, setOpen] = useState(false);
  const triggers = useQuery({
    queryKey: queryKey(pipeline.id),
    queryFn: () => api.triggers.list(pipeline.id),
  });
  const mine = (triggers.data ?? []).filter((t) => t.stageId === stage.id);
  return (
    <>
      <Button
        size="icon"
        variant="ghost"
        aria-label={`Automations for ${stage.name}`}
        title={`${mine.length} automation${mine.length === 1 ? '' : 's'}`}
        className="relative"
        onClick={() => setOpen(true)}
      >
        <Zap />
        {mine.length > 0 && (
          <span
            className="bg-primary text-primary-foreground absolute -top-0.5 -right-0.5 rounded-full px-1 text-[10px] leading-4 tabular-nums"
            data-testid="automation-count"
          >
            {mine.length}
          </span>
        )}
      </Button>
      {open && (
        <AutomationsDialog
          pipeline={pipeline}
          stage={stage}
          triggers={mine}
          loading={triggers.isPending}
          loadError={triggers.isError ? describeError(triggers.error) : null}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function AutomationsDialog({
  pipeline,
  stage,
  triggers,
  loading,
  loadError,
  onClose,
}: {
  pipeline: PipelineView;
  stage: PipelineStageView;
  triggers: TriggerView[];
  loading: boolean;
  loadError: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const session = useSession();
  const canCreate = session.can('automation.create');
  const canUpdate = session.can('automation.update');
  const canDelete = session.can('automation.delete');
  const [editing, setEditing] = useState<TriggerView | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<TriggerView | null>(null);
  const [rotating, setRotating] = useState<TriggerView | null>(null);
  const [running, setRunning] = useState<TriggerView | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // A signing secret is in an answer only once (create, becoming a URL call, rotate); `then` is
  // the run-existing offer that waits behind it.
  const [secret, setSecret] = useState<{ value: string; then: TriggerView | null } | null>(null);
  const canRun = (t: TriggerView) => canUpdate && t.enabled && t.kind !== 'stage_left';
  const done = async () => {
    setError(null);
    setNotice(null);
    await qc.invalidateQueries({ queryKey: queryKey(pipeline.id) });
  };
  const toggle = useMutation({
    mutationFn: (t: TriggerView) => api.triggers.setEnabled(t.id, !t.enabled),
    onSuccess: done,
    onError: (err) => setError(describeError(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.triggers.remove(id),
    onSuccess: done,
    onError: (err) => setError(describeError(err)),
  });
  const rotate = useMutation({
    mutationFn: (id: string) => api.triggers.rotateSecret(id),
    onSuccess: (t) => t.signingSecret && setSecret({ value: t.signingSecret, then: null }),
    onError: (err) => setError(describeError(err)),
  });
  const subject = pipeline.appliesTo;
  if (secret)
    return (
      <SecretDialog
        secret={secret.value}
        onClose={() => {
          setSecret(null);
          if (secret.then) setRunning(secret.then);
        }}
      />
    );
  if (rotating)
    return (
      <Dialog open onOpenChange={(o) => !o && setRotating(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rotate the signing secret of {rotating.name}?</DialogTitle>
            <DialogDescription>
              A new secret is made and shown once. The current one keeps signing for an hour, so the
              receiving system can switch over; after that, only the new one does.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRotating(null)}>
              Cancel
            </Button>
            <Button
              disabled={rotate.isPending}
              data-testid="confirm-rotate-secret"
              onClick={() => rotate.mutate(rotating.id, { onSettled: () => setRotating(null) })}
            >
              Rotate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  if (removing)
    return (
      <Dialog open onOpenChange={(o) => !o && setRemoving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {removing.name}?</DialogTitle>
            <DialogDescription>
              The automation stops and what it had planned is skipped. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              data-testid="confirm-delete-automation"
              onClick={() => remove.mutate(removing.id, { onSettled: () => setRemoving(null) })}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  if (running)
    return (
      <RunExistingDialog
        trigger={running}
        stage={stage}
        onClose={() => setRunning(null)}
        onDone={async (r) => {
          setRunning(null);
          await done();
          setNotice(
            `${running.name}: ${records(r.planned)} planned${r.existing ? `, ${r.existing} already were` : ''}.`,
          );
        }}
      />
    );
  if (editing)
    return (
      <TriggerDialog
        pipeline={pipeline}
        stage={stage}
        subject={subject}
        trigger={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={async (saved) => {
          setEditing(null);
          await done();
          // Offered once, on create: records already in the stage are left out unless chosen.
          const offer = editing === 'new' && canRun(saved) ? saved : null;
          if (saved.signingSecret) setSecret({ value: saved.signingSecret, then: offer });
          else if (offer) setRunning(offer);
        }}
      />
    );
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Automations on {stage.name}</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          They apply to records that reach this stage from now on; a new one can also run once for
          those already in it. Switching one off skips what it had planned; switching it back on
          does not bring those back.
        </p>
        {loading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : loadError ? (
          <p className="text-danger text-sm" role="alert">
            {loadError}
          </p>
        ) : (
          triggers.length === 0 && <p className="text-sm">No automations yet.</p>
        )}
        <ul className="divide-border flex flex-col divide-y" data-testid="automation-list">
          {triggers.map((t) => (
            <li key={t.id} className="flex items-start gap-2 py-2 text-sm" data-testid="automation">
              <label className="mt-0.5 flex items-center" title={t.enabled ? 'On' : 'Off'}>
                <input
                  type="checkbox"
                  aria-label={`${t.name} on`}
                  checked={t.enabled}
                  disabled={!canUpdate || toggle.isPending}
                  onChange={() => toggle.mutate(t)}
                />
              </label>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium" title={t.name}>
                  {t.name}
                </p>
                <p className="text-muted-foreground">
                  {describeWhen(t)} → {describeWhat(t)}
                  {describeSchedule(t) && ` · ${describeSchedule(t)}`}
                </p>
              </div>
              {canUpdate && t.actionType === 'http_request' && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-7"
                  aria-label={`Rotate the signing secret of ${t.name}`}
                  title="Rotate the signing secret (the old one keeps working for an hour)"
                  onClick={() => setRotating(t)}
                >
                  <KeyRound />
                </Button>
              )}
              {canUpdate && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-7"
                  aria-label={`Edit ${t.name}`}
                  onClick={() => setEditing(t)}
                >
                  <Pencil />
                </Button>
              )}
              {canDelete && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-7"
                  aria-label={`Delete ${t.name}`}
                  onClick={() => setRemoving(t)}
                >
                  <Trash2 />
                </Button>
              )}
            </li>
          ))}
        </ul>
        {error && (
          <p className="text-danger text-sm" role="alert">
            {error}
          </p>
        )}
        {notice && (
          <p className="text-sm" role="status">
            {notice}
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {canCreate && (
            <Button onClick={() => setEditing('new')}>
              <Plus /> Add automation
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const records = (n: number) => `${n} record${n === 1 ? '' : 's'}`;

/** The one time a trigger's signing secret is visible: after it is minted and after a rotation. */
function SecretDialog({ secret, onClose }: { secret: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch (err) {
      // No clipboard (permissions, an insecure origin): the secret is selectable below.
      void err;
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* Shown once: a stray click outside must not throw it away. */}
      <DialogContent onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Signing secret</DialogTitle>
          <DialogDescription>
            Give this to the receiving system so it can check each request really came from the CRM
            (X-CRM-Signature, as webhooks). It is shown only now; if it is lost, rotate it.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <code
            className="bg-muted min-w-0 flex-1 rounded-lg border px-3 py-2 font-mono text-xs break-all select-all"
            data-testid="trigger-secret"
          >
            {secret}
          </code>
          <Button variant="outline" size="sm" onClick={() => void copy()} aria-label="Copy secret">
            {copied ? <Check /> : <Copy />} {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>I have copied it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Offered right after an automation is created: runs it once for the records already in its stage. */
function RunExistingDialog({
  trigger: t,
  stage,
  onClose,
  onDone,
}: {
  trigger: TriggerView;
  stage: PipelineStageView;
  onClose: () => void;
  onDone: (result: TriggerRunExisting) => Promise<void>;
}) {
  const count = useQuery({
    queryKey: ['pipelines', 'triggers', 'existing', t.id],
    queryFn: () => api.triggers.runExisting(t.id, true),
    gcTime: 0,
  });
  const run = useMutation({
    mutationFn: () => api.triggers.runExisting(t.id, false),
    onSuccess: onDone,
  });
  const nothing = count.data?.planned === 0;
  useEffect(() => {
    if (nothing) onClose();
  }, [nothing, onClose]);
  const http = t.actionType === 'http_request';
  const skipped = count.data ? count.data.inStage - count.data.planned - count.data.existing : 0;
  const schedule = describeSchedule(t);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Run for records already in this stage?</DialogTitle>
          <DialogDescription>
            {t.name} applies to records that reach {stage.name} from now on. It can also run once
            for the records already there.
          </DialogDescription>
        </DialogHeader>
        {count.isPending ? (
          <p className="text-muted-foreground text-sm">Counting…</p>
        ) : count.isError ? (
          <p className="text-danger text-sm" role="alert">
            {describeError(count.error)}
          </p>
        ) : (
          <div className="flex flex-col gap-2 text-sm">
            <p data-testid="run-existing-count">
              {records(count.data.inStage)} in this stage now: {count.data.planned} would run
              {count.data.existing > 0 &&
                `, ${count.data.existing} already planned stay as they are`}
              {skipped > 0 && `, ${skipped} not planned (the date has passed or is empty)`}.
            </p>
            <ul className="list-disc pl-5">
              <li>
                {t.actionType === 'create_task'
                  ? `Each gets a task: “${t.actionConfig.titleTemplate}”.`
                  : t.actionType === 'create_note'
                    ? 'Each gets a note on its record.'
                    : `Each is sent to ${new URL(t.actionConfig.url).host}, shown as an automated task in My Tasks until it is sent.`}
              </li>
              {t.kind === 'stage_entered' && t.delayMinutes > 0 && (
                <li>
                  A record already here for {duration(t.delayMinutes)} or more runs at once; the
                  others when their wait ends.
                </li>
              )}
              {t.kind === 'relative_to_date' && (
                <li>Only records whose date is still ahead get a run, at its usual time.</li>
              )}
              {schedule && <li>Its days and hours still apply ({schedule}).</li>}
              <li>Records that leave the stage before their run is due are skipped.</li>
            </ul>
            <p className="text-danger font-medium">
              {http
                ? 'This cannot be undone: a request once sent cannot be recalled.'
                : 'This cannot be undone: the tasks or notes stay until someone deletes them.'}
            </p>
          </div>
        )}
        {run.isError && (
          <p className="text-danger text-sm" role="alert">
            {describeError(run.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" autoFocus onClick={onClose}>
            Not now
          </Button>
          <Button
            variant="destructive"
            disabled={!count.data || nothing || run.isPending}
            data-testid="confirm-run-existing"
            onClick={() => run.mutate()}
          >
            Run for {records(count.data?.planned ?? 0)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Wording ─────────────────────────────────────────────────────────────────────────────────

const UNITS = { minutes: 1, hours: 60, days: 1440 } as const;
type Unit = keyof typeof UNITS;

/** The largest unit a number of minutes is a whole count of. */
function split(minutes: number): { amount: number; unit: Unit } {
  const abs = Math.abs(minutes);
  if (abs > 0 && abs % 1440 === 0) return { amount: abs / 1440, unit: 'days' };
  if (abs > 0 && abs % 60 === 0) return { amount: abs / 60, unit: 'hours' };
  return { amount: abs, unit: 'minutes' };
}
const duration = (minutes: number) => {
  const { amount, unit } = split(minutes);
  return `${amount} ${amount === 1 ? unit.slice(0, -1) : unit}`;
};

/** `interviewDateTime` → "Interview date time". */
const humanise = (key: string) => {
  const words = key.replace(/([A-Z])/g, ' $1').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
/** `job.interviewDateTime` → "Job · Interview date time". */
const fieldLabel = (path: string) =>
  path
    .split('.')
    .filter((p) => p !== 'custom')
    .map(humanise)
    .join(' · ');

function describeWhen(t: TriggerView): string {
  if (t.kind === 'stage_left')
    return t.delayMinutes === 0
      ? 'Leaves this stage'
      : `${duration(t.delayMinutes)} after leaving this stage`;
  if (t.kind === 'relative_to_date')
    return `${duration(t.delayMinutes)} ${t.delayMinutes < 0 ? 'before' : 'after'} ${fieldLabel(t.dateField ?? '')}`;
  return t.delayMinutes === 0
    ? 'Enters this stage'
    : `${duration(t.delayMinutes)} after entering, if still here`;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const dayLabel = (iso: number) => DAY_LABELS[iso - 1]!;
const sameDays = (a: readonly number[], b: readonly number[]) =>
  a.length === b.length && b.every((d) => a.includes(d));

/** 540 → "09:00", 1440 → "24:00" (the window is minutes of the New York day). */
const toTime = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const fromTime = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return h! * 60 + m!;
};
// A time field cannot hold 24:00, so an end at 00:00 is midnight at the end of the day.
const untilMinute = (time: string) => fromTime(time) || 1440;

/** "Mon–Fri, 09:00–18:00 New York", or nothing when it may run any time. */
function describeSchedule(t: TriggerView): string {
  const days = [...t.allowedWeekdays].sort((a, b) => a - b);
  const window =
    t.allowedFromMinute !== null && t.allowedUntilMinute !== null
      ? `${toTime(t.allowedFromMinute)}–${toTime(t.allowedUntilMinute)}`
      : null;
  if (sameDays(days, ISO_WEEKDAYS) && !window) return '';
  const dayText = sameDays(days, ISO_WEEKDAYS)
    ? 'Every day'
    : sameDays(days, WORKING_WEEKDAYS)
      ? 'Mon–Fri'
      : days.map(dayLabel).join(', ');
  return `${dayText}${window ? `, ${window}` : ''} New York`;
}

function describeWhat(t: TriggerView): string {
  switch (t.actionType) {
    case 'create_task':
      return `Task${t.actionConfig.taskType ? ` (${TASK_TYPE_LABELS[t.actionConfig.taskType]})` : ''}: ${t.actionConfig.titleTemplate}`;
    case 'create_note':
      return 'Note';
    case 'http_request':
      return `${TASK_TYPE_LABELS[t.actionConfig.taskType]} via ${new URL(t.actionConfig.url).host}`;
  }
}

// ── Editor ──────────────────────────────────────────────────────────────────────────────────

type When = 'entered' | 'after' | 'left' | 'date';
type AssigneeRule = 'record_owner' | 'job_recruiter' | 'job_coordinator' | 'job_account_manager';
const ASSIGNEES: Record<AssigneeRule, string> = {
  record_owner: 'Record owner',
  job_recruiter: "Job's recruiter",
  job_coordinator: "Job's coordinator",
  job_account_manager: "Job's account manager",
};

// Every automation sends to the team's n8n funnel webhook unless the admin points it elsewhere.
const DEFAULT_HTTP_URL = 'https://n8n.remoteleverage.com/webhook/crm-funnel-updates';

/** The form's fields, flat: one place for each input, whatever the action. */
function initialForm(t: TriggerView | null) {
  const delay = split(t?.delayMinutes ?? 0);
  const when: When =
    t?.kind === 'stage_left'
      ? 'left'
      : t?.kind === 'relative_to_date'
        ? 'date'
        : t && t.delayMinutes > 0
          ? 'after'
          : 'entered';
  const task = t?.actionType === 'create_task' ? t.actionConfig : null;
  const note = t?.actionType === 'create_note' ? t.actionConfig : null;
  const http = t?.actionType === 'http_request' ? t.actionConfig : null;
  const due = split(task?.dueOffsetMinutes ?? 0);
  // No default: the type is what the receiver acts on, so the admin picks it.
  const automatedType: AutomatedType | '' = http?.taskType ?? '';
  return {
    name: t?.name ?? '',
    when,
    amount: when === 'after' || when === 'date' ? String(delay.amount) : '1',
    unit: when === 'after' || when === 'date' ? delay.unit : ('days' as Unit),
    // Only the API sets a delay after leaving; the form keeps it.
    leftDelay: when === 'left' ? t!.delayMinutes : 0,
    // A reminder ahead of a date (an interview) is the usual case.
    before: t ? t.delayMinutes < 0 : true,
    dateField: t?.dateField ?? '',
    actionType: t?.actionType ?? 'create_task',
    taskType: task?.taskType ?? '',
    title: task?.titleTemplate ?? '',
    description: task?.descriptionTemplate ?? '',
    assignee: task?.assignee.rule ?? 'record_owner',
    userId: task?.assignee.rule === 'user' ? task.assignee.userId : '',
    dueAmount: task?.dueOffsetMinutes != null ? String(due.amount) : '',
    dueUnit: task?.dueOffsetMinutes != null ? due.unit : ('days' as Unit),
    noteBody: note?.bodyTemplate ?? '',
    automatedType,
    method: http?.method ?? 'POST',
    url: http?.url ?? DEFAULT_HTTP_URL,
    days: [...(t?.allowedWeekdays ?? ISO_WEEKDAYS)] as number[],
    from: t?.allowedFromMinute != null ? toTime(t.allowedFromMinute) : '',
    until: t?.allowedUntilMinute != null ? toTime(t.allowedUntilMinute % 1440) : '',
  };
}
type Form = ReturnType<typeof initialForm>;
type AutomatedType = (typeof AUTOMATED_TASK_TYPES)[number];

function toBody(
  f: Form,
  pipeline: PipelineView,
  stage: PipelineStageView,
  subject: TriggerSubject,
  /** Kept as they are: the list switches a trigger on and off, and the editor has no condition field. */
  kept: Pick<TriggerView, 'condition' | 'enabled'>,
): TriggerBody {
  const minutes = Number(f.amount) * UNITS[f.unit];
  // Only an automated task waits for its days and hours; a task or note runs at once.
  const http = f.actionType === 'http_request';
  const windowed = http && f.from !== '' && f.until !== '';
  const rule = {
    name: f.name.trim(),
    pipelineId: pipeline.id,
    stageId: stage.id,
    subject,
    kind:
      f.when === 'left' ? 'stage_left' : f.when === 'date' ? 'relative_to_date' : 'stage_entered',
    delayMinutes:
      f.when === 'after'
        ? minutes
        : f.when === 'date'
          ? f.before
            ? -minutes
            : minutes
          : f.when === 'left'
            ? f.leftDelay
            : 0,
    dateField: f.when === 'date' ? f.dateField : null,
    allowedWeekdays: http ? [...f.days].sort((a, b) => a - b) : [...ISO_WEEKDAYS],
    allowedFromMinute: windowed ? fromTime(f.from) : null,
    allowedUntilMinute: windowed ? untilMinute(f.until) : null,
    ...kept,
  } as const;
  if (f.actionType === 'create_note')
    return { ...rule, actionType: 'create_note', actionConfig: { bodyTemplate: f.noteBody } };
  if (f.actionType === 'http_request')
    return {
      ...rule,
      actionType: 'http_request',
      actionConfig: {
        method: f.method,
        url: f.url.trim(),
        // Saving waits for a type (the Save button).
        taskType: f.automatedType as AutomatedType,
      },
    };
  return {
    ...rule,
    actionType: 'create_task',
    actionConfig: {
      titleTemplate: f.title,
      descriptionTemplate: f.description.trim() ? f.description : null,
      assignee: f.assignee === 'user' ? { rule: 'user', userId: f.userId } : { rule: f.assignee },
      dueOffsetMinutes: f.dueAmount ? Number(f.dueAmount) * UNITS[f.dueUnit] : null,
      taskType: (f.taskType || null) as (typeof MANUAL_TASK_TYPES)[number] | null,
    },
  };
}

/** What a run of the automation as the form stands would send, around example records. */
function previewPayload(
  f: Form,
  taskType: AutomatedType,
  pipeline: PipelineView,
  stage: PipelineStageView,
  subject: TriggerSubject,
) {
  const rule = toBody(f, pipeline, stage, subject, { condition: null, enabled: true });
  const delay = rule.delayMinutes ?? 0;
  return triggerPayloadExample({
    name: rule.name,
    subject,
    kind: rule.kind,
    delayMinutes: Number.isFinite(delay) ? delay : 0,
    dateField: rule.dateField ?? null,
    taskType,
    pipeline,
    stage,
    schedule: {
      allowedWeekdays: rule.allowedWeekdays ?? ISO_WEEKDAYS,
      allowedFromMinute: rule.allowedFromMinute ?? null,
      allowedUntilMinute: rule.allowedUntilMinute ?? null,
    },
  });
}

/** The form's label for a path of the body, so a problem names the field to fix. */
const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  kind: 'When',
  delayMinutes: 'How long',
  dateField: 'Date',
  allowedWeekdays: 'Runs on',
  allowedFromMinute: 'From',
  allowedUntilMinute: 'Until',
  'actionConfig.taskType': 'Automated task type',
  'actionConfig.titleTemplate': 'Title',
  'actionConfig.descriptionTemplate': 'Description',
  'actionConfig.assignee': 'Assign to',
  'actionConfig.assignee.userId': 'Member',
  'actionConfig.dueOffsetMinutes': 'Due',
  'actionConfig.bodyTemplate': 'Note',
  'actionConfig.method': 'Method',
  'actionConfig.url': 'URL',
};
/** `actionConfig.url` (ours) or `/actionConfig/url` (the API's request validation) → its label. */
const fieldName = (path: string) => {
  const key = path.replace(/^\//, '').replaceAll('/', '.');
  return FIELD_LABELS[key] ?? key;
};

/** What keeps the body from saving, by the contract the API checks it with; null when nothing. */
function formProblem(f: Form, body: TriggerBody): string | null {
  if (f.when === 'after' && !((body.delayMinutes ?? 0) >= 1)) return 'How long: at least 1 minute';
  const issue = triggerInputSchema.safeParse(body, {
    error: (iss) =>
      iss.origin === 'number' && (iss.code === 'too_big' || iss.code === 'too_small')
        ? `0 to ${duration(TRIGGER_MAX_DELAY_MINUTES)}`
        : iss.origin === 'string' && iss.code === 'too_small'
          ? 'required'
          : iss.code === 'invalid_value' || (iss.code === 'invalid_format' && iss.format === 'uuid')
            ? 'choose one'
            : undefined,
  }).error?.issues[0];
  return issue ? `${fieldName(issue.path.join('.'))}: ${issue.message}` : null;
}

/** The API's refusal, with the first field it names. */
function describeRefusal(err: unknown): string {
  const details = err instanceof ApiError ? err.details : undefined;
  const issues = Array.isArray(details)
    ? details
    : (details as { issues?: unknown } | undefined)?.issues;
  const first = (Array.isArray(issues) ? issues[0] : undefined) as
    { path?: unknown; message?: unknown } | undefined;
  return typeof first?.path === 'string' && first.path
    ? `${describeError(err)}: ${fieldName(first.path)}: ${String(first.message)}`
    : describeError(err);
}

function TriggerDialog({
  pipeline,
  stage,
  subject,
  trigger,
  onClose,
  onSaved,
}: {
  pipeline: PipelineView;
  stage: PipelineStageView;
  subject: TriggerSubject;
  trigger: TriggerView | null;
  onClose: () => void;
  onSaved: (saved: TriggerWithSecretView) => Promise<void>;
}) {
  const [initial] = useState(() => initialForm(trigger));
  const [f, setF] = useState(initial);
  const set = (patch: Partial<Form>) => setF((prev) => ({ ...prev, ...patch }));
  const [error, setError] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const dirty = JSON.stringify(f) !== JSON.stringify(initial);
  const requestClose = () => (dirty ? setDiscarding(true) : onClose());
  const { members } = useMembers();
  const fields = useTemplateFields(subject);
  const dates = TRIGGER_DATE_FIELDS[subject];
  const body = toBody(f, pipeline, stage, subject, {
    condition: trigger?.condition ?? null,
    enabled: trigger?.enabled ?? true,
  });
  const save = useMutation({
    mutationFn: () =>
      trigger ? api.triggers.replace(trigger.id, body) : api.triggers.create(body),
    onSuccess: onSaved,
    onError: (err) => setError(describeRefusal(err)),
  });
  const scheduleProblem =
    f.actionType !== 'http_request'
      ? null
      : f.days.length === 0
        ? 'Choose at least one day.'
        : (f.from === '') !== (f.until === '')
          ? 'Set both times, or neither for all day.'
          : f.from !== '' && fromTime(f.from) >= untilMinute(f.until)
            ? 'The end must be after the start, the same day.'
            : null;
  const problem = scheduleProblem ? null : formProblem(f, body);
  const assignees = Object.entries(ASSIGNEES).filter(
    ([rule]) => subject === 'shortlist' || subject === 'job' || !rule.startsWith('job_'),
  );
  return (
    <Dialog open onOpenChange={(open) => !open && requestClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {trigger ? `Edit ${trigger.name}` : `New automation on ${stage.name}`}
          </DialogTitle>
        </DialogHeader>
        <form
          id="trigger-form"
          className="flex flex-col gap-4"
          // Checked below, by field name: a browser check on the folded URL could not show itself.
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            Name
            <Input value={f.name} onChange={(e) => set({ name: e.target.value })} autoFocus />
          </label>

          <fieldset className="flex flex-col gap-2 text-sm">
            <legend className="mb-1 font-medium">When</legend>
            <select
              aria-label="When"
              className={nativeSelectClass}
              value={f.when}
              onChange={(e) => set({ when: e.target.value as When })}
            >
              <option value="entered">It enters this stage</option>
              <option value="after">Some time after it enters, if still here</option>
              <option value="left">It leaves this stage</option>
              {(dates.length > 0 || f.when === 'date') && (
                <option value="date">Some time before or after a date, while here</option>
              )}
            </select>
            {f.when === 'left' && f.leftDelay > 0 && (
              <p className="text-muted-foreground">
                {duration(f.leftDelay)} after it leaves (set through the API, kept as it is).
              </p>
            )}
            {(f.when === 'after' || f.when === 'date') && (
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  aria-label="How long"
                  type="number"
                  // On the date itself is a valid reminder; a wait after entering is not.
                  min={f.when === 'date' ? 0 : 1}
                  required
                  className="w-24"
                  value={f.amount}
                  onChange={(e) => set({ amount: e.target.value })}
                />
                <select
                  aria-label="Unit"
                  className={cn(nativeSelectClass, 'w-auto min-w-fit')}
                  value={f.unit}
                  onChange={(e) => set({ unit: e.target.value as Unit })}
                >
                  {Object.keys(UNITS).map((u) => (
                    <option key={u}>{u}</option>
                  ))}
                </select>
                {f.when === 'date' && (
                  <>
                    <select
                      aria-label="Before or after"
                      className={cn(nativeSelectClass, 'w-auto min-w-fit')}
                      value={f.before ? 'before' : 'after'}
                      onChange={(e) => set({ before: e.target.value === 'before' })}
                    >
                      <option value="before">before</option>
                      <option value="after">after</option>
                    </select>
                    <select
                      aria-label="Date"
                      className={cn(nativeSelectClass, 'w-auto min-w-fit flex-1')}
                      value={f.dateField}
                      onChange={(e) => set({ dateField: e.target.value })}
                    >
                      <option value="">Choose a date…</option>
                      {dates.map((d) => (
                        <option key={d} value={d}>
                          {fieldLabel(d)}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            )}
          </fieldset>

          <fieldset className="flex flex-col gap-2 text-sm">
            <legend className="mb-1 font-medium">Do</legend>
            <select
              aria-label="Action"
              className={nativeSelectClass}
              value={f.actionType}
              onChange={(e) => set({ actionType: e.target.value as Form['actionType'] })}
            >
              <option value="create_task">Create a task</option>
              <option value="create_note">Add a note</option>
              <option value="http_request">Run an automated task (call a URL)</option>
            </select>

            {f.actionType === 'create_task' && (
              <>
                <label className="flex flex-col gap-1">
                  Task type
                  <select
                    className={nativeSelectClass}
                    value={f.taskType}
                    onChange={(e) => set({ taskType: e.target.value })}
                  >
                    <option value="">No type</option>
                    {MANUAL_TASK_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {TASK_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </label>
                <TemplateField
                  label="Title"
                  value={f.title}
                  onChange={(title) => set({ title })}
                  fields={fields}
                  rows={1}
                />
                <TemplateField
                  label="Description"
                  value={f.description}
                  onChange={(description) => set({ description })}
                  fields={fields}
                />
                <label className="flex flex-col gap-1">
                  Assign to
                  <select
                    className={nativeSelectClass}
                    value={f.assignee}
                    onChange={(e) => set({ assignee: e.target.value as Form['assignee'] })}
                  >
                    {assignees.map(([rule, label]) => (
                      <option key={rule} value={rule}>
                        {label}
                      </option>
                    ))}
                    <option value="user">A member…</option>
                  </select>
                </label>
                {f.assignee === 'user' && (
                  <select
                    aria-label="Member"
                    className={nativeSelectClass}
                    value={f.userId}
                    onChange={(e) => set({ userId: e.target.value })}
                  >
                    <option value="">Choose a member…</option>
                    {/* The stored assignee stays listed even after they leave, as in the task dialog. */}
                    {members
                      .filter((m) => m.status === 'active' || m.id === f.userId)
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name ?? m.email}
                        </option>
                      ))}
                  </select>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  Due
                  <Input
                    aria-label="Due after"
                    type="number"
                    min={0}
                    placeholder="—"
                    className="w-24"
                    value={f.dueAmount}
                    onChange={(e) => set({ dueAmount: e.target.value })}
                  />
                  <select
                    aria-label="Due unit"
                    className={cn(nativeSelectClass, 'w-auto min-w-fit')}
                    value={f.dueUnit}
                    onChange={(e) => set({ dueUnit: e.target.value as Unit })}
                  >
                    {Object.keys(UNITS).map((u) => (
                      <option key={u}>{u}</option>
                    ))}
                  </select>
                  <span className="text-muted-foreground">
                    after it is created (empty: no due date)
                  </span>
                </div>
              </>
            )}

            {f.actionType === 'create_note' && (
              <TemplateField
                label="Note"
                value={f.noteBody}
                onChange={(noteBody) => set({ noteBody })}
                fields={fields}
              />
            )}

            {f.actionType === 'http_request' && (
              <>
                <label className="flex flex-col gap-1">
                  Automated task type
                  <select
                    className={nativeSelectClass}
                    required
                    value={f.automatedType}
                    onChange={(e) =>
                      set({ automatedType: e.target.value as Form['automatedType'] })
                    }
                  >
                    <option value="" disabled>
                      Choose what the receiver does
                    </option>
                    {AUTOMATED_TASK_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {TASK_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex gap-2">
                  <select
                    aria-label="Method"
                    className={cn(nativeSelectClass, 'w-auto min-w-fit')}
                    value={f.method}
                    onChange={(e) => set({ method: e.target.value as Form['method'] })}
                  >
                    <option>POST</option>
                    <option>PUT</option>
                  </select>
                  <Input
                    aria-label="URL"
                    type="url"
                    placeholder="https://"
                    value={f.url}
                    onChange={(e) => set({ url: e.target.value })}
                  />
                </div>
                <Collapsible label="What is sent">
                  {f.automatedType ? (
                    <>
                      <p className="text-muted-foreground text-xs">
                        {subject === 'candidate'
                          ? 'This automation, what happened, and the candidate'
                          : "This automation, what happened, and the record's project with everything related"}
                        , as JSON (the records here are examples):
                      </p>
                      <pre
                        aria-label="Example payload"
                        className="bg-muted max-h-64 overflow-auto rounded p-2 font-mono text-xs"
                      >
                        {JSON.stringify(
                          previewPayload(f, f.automatedType, pipeline, stage, subject),
                          null,
                          2,
                        )}
                      </pre>
                    </>
                  ) : (
                    <p className="text-muted-foreground text-xs">
                      Choose the automated task type to see what is sent.
                    </p>
                  )}
                </Collapsible>
                <fieldset className="flex flex-col gap-2">
                  <legend className="mb-1">Runs on</legend>
                  <div className="flex flex-wrap items-center gap-3">
                    {ISO_WEEKDAYS.map((d) => (
                      <label key={d} className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={f.days.includes(d)}
                          onChange={(e) =>
                            set({
                              days: e.target.checked
                                ? [...f.days, d]
                                : f.days.filter((x) => x !== d),
                            })
                          }
                        />
                        {dayLabel(d)}
                      </label>
                    ))}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => set({ days: [...ISO_WEEKDAYS] })}
                    >
                      Every day
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => set({ days: [...WORKING_WEEKDAYS] })}
                    >
                      Weekdays
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    Between
                    <Input
                      aria-label="From"
                      type="time"
                      className="w-32"
                      value={f.from}
                      onChange={(e) => set({ from: e.target.value })}
                    />
                    and
                    <Input
                      aria-label="Until"
                      type="time"
                      className="w-32"
                      value={f.until}
                      onChange={(e) => set({ until: e.target.value })}
                    />
                    <span className="text-muted-foreground">
                      New York time (empty: all day; until 00:00: to midnight)
                    </span>
                  </div>
                  {scheduleProblem && (
                    <p className="text-danger text-xs" role="alert">
                      {scheduleProblem}
                    </p>
                  )}
                </fieldset>
                <p className="text-muted-foreground text-xs">
                  Each request is signed (X-CRM-Signature, as webhooks); the secret is shown once,
                  when you save. Until it runs, it shows in My Tasks as an automated task due at
                  that time. Due on another day or hour, it waits until{' '}
                  {f.from || toTime(TRIGGER_DEFAULT_OPEN_MINUTE)} New York on the next allowed day
                  (a reminder before a date moves to the one before instead).
                </p>
              </>
            )}
          </fieldset>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
        </form>
        {discarding ? (
          <DialogFooter className="items-center" data-testid="confirm-discard">
            <span className="mr-auto text-sm">Discard your changes?</span>
            <Button variant="ghost" onClick={() => setDiscarding(false)}>
              Keep editing
            </Button>
            <Button variant="destructive" onClick={onClose}>
              Discard
            </Button>
          </DialogFooter>
        ) : (
          <DialogFooter className="items-center">
            {problem && (
              <p className="text-muted-foreground mr-auto text-xs" data-testid="trigger-problem">
                {problem}
              </p>
            )}
            <Button variant="ghost" onClick={requestClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="trigger-form"
              disabled={problem !== null || scheduleProblem !== null || save.isPending}
            >
              {trigger ? 'Save' : 'Add automation'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Templates ───────────────────────────────────────────────────────────────────────────────

type TemplateFields = { options: FilterOption[]; groups: OptionGroup[] };

/** The merge fields a subject can use, one group per entity: its catalogue, plus the candidate custom fields. */
function useTemplateFields(subject: TriggerSubject): TemplateFields {
  const session = useSession();
  const entities = TEMPLATE_ENTITIES_BY_SUBJECT[subject];
  // Templates reach the candidate's custom fields only.
  const custom = entities.includes('candidate');
  const defs = useQuery({
    queryKey: ['field-definitions', 'candidates'],
    queryFn: () => api.fieldDefinitions.list('candidates'),
    enabled: custom && session.isEnabled('custom_fields.enabled'),
  });
  return {
    groups: entities.map((e) => ({ group: e, label: humanise(e) })),
    options: [
      ...entities.flatMap((e) =>
        TEMPLATE_FIELDS[e].map((k) => ({ value: `${e}.${k}`, label: humanise(k), group: e })),
      ),
      ...(custom ? (defs.data ?? []) : [])
        // The API refuses one hidden from any role: the task or note would show it to that role.
        .filter((d) => !Object.values(d.visibility).includes('hidden'))
        .map((d) => ({ value: `candidate.custom.${d.key}`, label: d.label, group: 'candidate' })),
    ],
  };
}

/** A folded section drawn like the sidebar's groups: small caps label, chevron, eased open. */
function Collapsible({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex items-center justify-between rounded-md py-1 text-[10px] font-semibold tracking-wider uppercase transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        {label}
        <ChevronDown
          className={cn(
            'size-3 transition-transform duration-200 ease-out motion-reduce:transition-none',
            !open && '-rotate-90',
          )}
          aria-hidden
        />
      </button>
      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
        inert={!open}
        aria-hidden={!open}
      >
        <div className="-m-1 flex min-h-0 flex-col gap-2 overflow-hidden p-1">{children}</div>
      </div>
    </div>
  );
}

/** A textarea with "Insert field": puts `{{entity.field}}` where the cursor is. */
function TemplateField({
  label,
  value,
  onChange,
  fields,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  fields: TemplateFields;
  rows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const id = useId();
  const [picking, setPicking] = useState(false);
  const caret = useRef<number | null>(null);
  const insert = (path: string) => {
    const el = ref.current;
    const at = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? at;
    const token = `{{${path}}}`;
    onChange(value.slice(0, at) + token + value.slice(end));
    caret.current = at + token.length;
  };
  // After a pick, focus goes back to the text just after the field, so the next one follows it.
  const restoreCaret = (e: Event) => {
    const el = ref.current;
    const at = caret.current;
    caret.current = null;
    if (!el || at === null) return;
    e.preventDefault();
    el.focus();
    el.setSelectionRange(at, at);
  };
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id}>{label}</label>
        {/* Modal: the dialog's scroll lock would otherwise swallow the wheel over the portalled list. */}
        <Popover modal open={picking} onOpenChange={setPicking}>
          <PopoverTrigger
            aria-label={`Insert field into ${label}`}
            className={cn(selectTriggerClass, 'h-7 w-40 px-2 text-xs')}
          >
            <span>Insert field…</span>
            <ChevronDown className="text-muted-foreground size-3" />
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="flex w-64 flex-col gap-1.5 p-1.5"
            onCloseAutoFocus={restoreCaret}
          >
            <OptionSearch
              label="Fields"
              options={fields.options}
              groups={fields.groups}
              folded
              single
              selected={[]}
              onChange={([path]) => {
                if (path) insert(path);
                setPicking(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      <Textarea
        id={id}
        ref={ref}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
